// Utility functions for user data

/**
 * Generate user initials from user object
 * Handles multiple name formats:
 * - "LastName, FirstName" (e.g., "Bharamanaik, Shilpa" → "SB")
 * - "FirstName LastName" (e.g., "John Doe" → "JD")
 * - Single name (e.g., "John" → "J")
 * 
 * @param {Object} user - User object from authentication
 * @returns {string} - User initials (2 letters or 1 letter)
 */
export const getUserInitials = (user) => {
  if (!user) return 'U'; // Default if no user
  
  // Get the name from user object (try different fields)
  const fullName = user.name || 
                   user.displayName || 
                   (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '') ||
                   (user.givenName && user.surname ? `${user.givenName} ${user.surname}` : '') ||
                   user.email?.split('@')[0] || 
                   '';
  
  if (!fullName.trim()) return 'U';
  
  // Check if name is in "LastName, FirstName" format (contains comma)
  if (fullName.includes(',')) {
    const parts = fullName.split(',').map(part => part.trim()).filter(part => part.length > 0);
    if (parts.length >= 2) {
      // parts[0] = LastName, parts[1] = FirstName
      const firstNameInitial = parts[1].charAt(0).toUpperCase(); // S from Shilpa
      const lastNameInitial = parts[0].charAt(0).toUpperCase();  // B from Bharamanaik
      return firstNameInitial + lastNameInitial; // SB
    }
  }
  
  // Handle normal "FirstName LastName" format
  const nameParts = fullName.trim().split(' ').filter(part => part.length > 0);
  
  if (nameParts.length === 0) return 'U';
  if (nameParts.length === 1) {
    // Single name - return first letter
    return nameParts[0].charAt(0).toUpperCase();
  } else {
    // Multiple names - return first letter of first and last name
    const firstName = nameParts[0].charAt(0).toUpperCase();
    const lastName = nameParts[nameParts.length - 1].charAt(0).toUpperCase();
    return firstName + lastName;
  }
};

/**
 * Get user's first name only
 * @param {Object} user - User object from authentication
 * @returns {string} - User's first name
 */
export const getUserFirstName = (user) => {
  if (!user) return 'User';
  
  // Handle "LastName, FirstName" format
  if (user.name && user.name.includes(',')) {
    const parts = user.name.split(',').map(part => part.trim());
    if (parts.length >= 2) return parts[1].split(' ')[0];
  }
  
  const fullName = user.name || user.displayName || 
    (user.firstName ? user.firstName : '') ||
    (user.givenName ? user.givenName : '') ||
    user.email?.split('@')[0] || 'User';
  
  return fullName.trim().split(' ')[0];
};

/**
 * Get user's display name
 * @param {Object} user - User object from authentication
 * @returns {string} - User's full name or email
 */
export const getUserDisplayName = (user) => {
  if (!user) return 'User';
  
  // Handle "LastName, FirstName" format - reverse it to "FirstName LastName"
  if (user.name && user.name.includes(',')) {
    const parts = user.name.split(',').map(part => part.trim());
    if (parts.length >= 2) {
      return `${parts[1]} ${parts[0]}`; // "Shilpa Bharamanaik"
    }
  }
  
  return user.name || 
         user.displayName || 
         (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : '') ||
         (user.givenName && user.surname ? `${user.givenName} ${user.surname}` : '') ||
         user.email || 
         'User';
};
